import React, { useCallback, useState, useEffect } from "react";
// @mui
import {
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  List,
  ListItem,
  Stack,
} from "@mui/material";
// __apis__
import { staticStretchesFetcher } from "src/__apis__/workout";
// Recoil
import { useRecoilState, useRecoilValue } from "recoil";
// atoms
import workoutPlanAtom from "src/recoil/atoms/workoutPlanAtom";
import userAtom from "src/recoil/atoms/userAtom";
// Iconfiy
import { Icon } from "@iconify/react";

function PrintableWorkout() {
  const handlePrint = useCallback(() => {
    const printContent = document.getElementById("print-content").innerHTML;
    const originalContent = document.body.innerHTML;
    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  }, []);

  const [workoutPlan, setWorkoutPlan] = useRecoilState(workoutPlanAtom);

  const mergeData = () => {
    const mergedData = [
      ...workoutPlan.strengthExercises,
      ...workoutPlan.supersets,
    ].sort((a, b) => parseInt(a.order) - parseInt(b.order));

    return mergedData;
  };

  const [staticStretches, setStaticStretches] = useState([]);

  const user = useRecoilValue(userAtom);

  const fetchStaticStretches = useCallback(async () => {
    await staticStretchesFetcher(user?.client_data_id)
      .then((response) => {
        setStaticStretches(response);
      })
      .catch((error) => {
        console.log("Error fetching static stretches", error);
      });
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchStaticStretches();
    }
  }, [user]);

  return (
    <Box>
      <Box sx={{ textAlign: "center", marginTop: 2 }}>
        <Tooltip title="print">
          <IconButton onClick={handlePrint} sx={{ color: "primary.main" }}>
            <Icon icon="fluent:print-32-regular" fontSize={40} />
          </IconButton>
        </Tooltip>
      </Box>
      <Grid container spacing={3} id="print-content">
        <Grid item xs={12}>
          <Card sx={{ mt: 5 }}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h2" gutterBottom color="primary.main">
                    Warm Up
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <List>
                    {workoutPlan.warmUpExercises.map((exerciseData, index) => (
                      <ListItem key={index} disablePadding>
                        <Typography variant="h4" gutterBottom>
                          • {exerciseData?.exercise?.name}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h2" gutterBottom color="primary.main">
                    Exercise
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <List>
                    {mergeData().map((exerciseData, index) =>
                      exerciseData?.label ? (
                        <Box>
                          <Box
                            sx={{
                              border: 1,
                              width: "fit-content",
                              p: 1,
                              borderRadius: 2,
                              borderColor: "primary.main",
                              borderStyle: "dashed",
                              mb: 2,
                            }}
                          >
                            <Stack direction="row" gap={1}>
                              <Typography
                                variant="h4"
                                gutterBottom
                                color="primary.main"
                              >
                                {exerciseData?.order > 10
                                  ? exerciseData?.order
                                  : `0${exerciseData?.order}`}
                              </Typography>
                              <Typography variant="h4" gutterBottom>
                                {exerciseData?.label}
                              </Typography>
                            </Stack>
                          </Box>
                          {exerciseData?.exercises?.map((data, index) => (
                            <Box>
                              <Typography variant="h4" gutterBottom>
                                • {data?.exercise?.name}
                              </Typography>
                              <Stack direction="row" sx={{ ml: 3 }} gap={3}>
                                <Typography variant="h6" gutterBottom>
                                  Sets:{" "}
                                  <Typography
                                    component="span"
                                    color="primary.main"
                                  >
                                    {data?.sets}
                                  </Typography>
                                </Typography>
                                <Typography variant="h6" gutterBottom>
                                  Reps:{" "}
                                  <Typography
                                    component="span"
                                    color="primary.main"
                                  >
                                    {data?.reps}
                                  </Typography>
                                </Typography>
                                <Typography variant="h6" gutterBottom>
                                  Rest:{" "}
                                  {data?.exercise?.recommended_rest_time ? (
                                    <Typography
                                      component="span"
                                      color="primary.main"
                                    >
                                      {data.exercise.recommended_rest_time.replace(
                                        /[^\d-]/g,
                                        ""
                                      )}{" "}
                                      Seconds
                                    </Typography>
                                  ) : (
                                    <Typography
                                      variant="h6"
                                      component="span"
                                      color="primary.main"
                                    >
                                      Not Available
                                    </Typography>
                                  )}
                                </Typography>
                              </Stack>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Box>
                          <ListItem key={index} disablePadding>
                            <Typography variant="h4" gutterBottom>
                              • {exerciseData?.exercise?.name}
                            </Typography>
                          </ListItem>
                          <Stack direction="row" sx={{ ml: 3 }} gap={3}>
                            <Typography variant="h6" gutterBottom>
                              Sets:{" "}
                              <Typography component="span" color="primary.main">
                                {exerciseData?.sets}
                              </Typography>
                            </Typography>
                            <Typography variant="h6" gutterBottom>
                              Reps:{" "}
                              <Typography component="span" color="primary.main">
                                {exerciseData?.reps}
                              </Typography>
                            </Typography>
                            <Typography variant="h6" gutterBottom>
                              Rest:{" "}
                              {exerciseData?.exercise?.recommended_rest_time ? (
                                <Typography
                                  component="span"
                                  color="primary.main"
                                >
                                  {exerciseData.exercise.recommended_rest_time.replace(
                                    /[^\d-]/g,
                                    ""
                                  )}{" "}
                                  Seconds
                                </Typography>
                              ) : (
                                <Typography
                                  variant="h6"
                                  component="span"
                                  color="primary.main"
                                >
                                  Not Available
                                </Typography>
                              )}
                            </Typography>
                          </Stack>
                        </Box>
                      )
                    )}
                  </List>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h2" gutterBottom color="primary.main">
                    Cardio
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <List>
                    {workoutPlan.cardioExercises.map((exerciseData, index) => (
                      <Box>
                        <ListItem key={index} disablePadding>
                          <Typography variant="h4" gutterBottom>
                            • {exerciseData?.exercise?.name}
                          </Typography>
                        </ListItem>
                        <Stack direction="row" sx={{ ml: 3 }} gap={1}>
                          <Typography variant="h6">Duration:</Typography>
                          <Typography variant="h6" color="primary.main">
                            {exerciseData?.duration} Mins
                          </Typography>
                        </Stack>
                      </Box>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ mt: 5 }}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h2" gutterBottom color="primary.main">
                    Post Workout Stretches
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <List>
                    {staticStretches.map((exercise, index) => (
                      <ListItem key={index} disablePadding>
                        <Typography variant="h4" gutterBottom>
                          • {exercise?.name}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default PrintableWorkout;
